import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import PATH_PAGE from '@/router/path';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import { Link } from 'react-router-dom';
import { PlusIcon, Server, RefreshCw, Loader2 } from 'lucide-react';
import { useListSSHInstances } from '@/service/gpu-instance/hook';
import { SSHInstanceStatus, SSHInstanceStatusLabel } from '@/service/gpu-instance/type';
import { useState, useEffect } from 'react';
import { DateRange } from 'react-day-picker';
import { useDebounce } from '@/hooks/useDebounce';
import { isDateInRange } from '@/utils/date';
import InstanceSearch from './instance-search';

type FormSchemaFilter = {
  searchString: string;
  status: string[];
  date: DateRange | undefined;
};

const STORAGE_KEY_PREFIX = 'gpu-instances-status-filter';

const getStatusBadgeVariant = (status: SSHInstanceStatus) => {
  switch (status) {
    case SSHInstanceStatus.Running:
    case SSHInstanceStatus.SSHReady:
      return 'success';
    case SSHInstanceStatus.Pending:
      return 'warning';
    case SSHInstanceStatus.Failed:
      return 'destructive';
    case SSHInstanceStatus.Completed:
    case SSHInstanceStatus.Cancelled:
    case SSHInstanceStatus.Terminated:
      return 'secondary';
    default:
      return 'default';
  }
};

const formatDate = (timestamp: number | null) => {
  if (!timestamp) return '-';
  return new Date(timestamp * 1000).toLocaleString();
};

const GPUInstanceList = () => {
  const { workspaceId } = useParamWorkspaceId();
  const { data: instances, isLoading, isFetching, refetch } = useListSSHInstances();

  // Load saved status filter from localStorage on mount
  const getSavedStatusFilter = (): string[] => {
    if (!workspaceId) return [];
    try {
      const saved = localStorage.getItem(`${STORAGE_KEY_PREFIX}-${workspaceId}`);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading status filter from localStorage:', error);
      return [];
    }
  };

  const [filter, setFilter] = useState<FormSchemaFilter>({
    searchString: '',
    status: getSavedStatusFilter(),
    date: undefined,
  });
  const debouncedSearchString = useDebounce(filter.searchString, 500);

  // Save status filter to localStorage whenever it changes
  useEffect(() => {
    if (!workspaceId) return;
    try {
      localStorage.setItem(
        `${STORAGE_KEY_PREFIX}-${workspaceId}`,
        JSON.stringify(filter.status)
      );
    } catch (error) {
      console.error('Error saving status filter to localStorage:', error);
    }
  }, [filter.status, workspaceId]);

  // Filter instances based on search, status, and date
  const filteredInstances = instances?.filter((instance) => {
    // Case-insensitive substring search
    if (debouncedSearchString) {
      const searchLower = debouncedSearchString.toLowerCase();
      const taskIdLower = instance.task_id.toLowerCase();
      const projectNameLower = instance.project_name?.toLowerCase() || '';

      if (
        !taskIdLower.includes(searchLower) &&
        !projectNameLower.includes(searchLower)
      )
        return false;
    }
    if (filter.status.length > 0 && !filter.status.includes(instance.status))
      return false;
    if (instance.created_at && !isDateInRange(new Date(instance.created_at * 1000), filter.date)) return false;
    return true;
  });

  // Calculate total active instances (Running or SSH Ready)
  const totalActiveInstances = instances?.filter(
    (instance) => instance.status === SSHInstanceStatus.Running || instance.status === SSHInstanceStatus.SSHReady
  ).length || 0;

  const isFoundInstances = instances && instances.length > 0;

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">GPU Instances</h1>
          <div className="rounded-full bg-primary/10 px-3 py-1">
            <span className="text-sm font-medium text-primary">
              {totalActiveInstances} Active
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`size-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Link to={PATH_PAGE.dashboard.gpuInstance.create(workspaceId)}>
            <Button size="sm">
              <PlusIcon className="size-4 mr-2" />
              Create Instance
            </Button>
          </Link>
        </div>
      </div>

      {isFoundInstances && (
        <div className="mb-6">
          <InstanceSearch filter={filter} onFilterChange={setFilter} />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Instances</CardTitle>
          <CardDescription>
            View and manage all your GPU instances
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : !instances || instances.length === 0 ? (
            <div className="text-center py-12">
              <Server className="size-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No instances yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first GPU instance to get started
              </p>
              <Link to={PATH_PAGE.dashboard.gpuInstance.create(workspaceId)}>
                <Button>
                  <PlusIcon className="size-4 mr-2" />
                  Create Instance
                </Button>
              </Link>
            </div>
          ) : !filteredInstances || filteredInstances.length === 0 ? (
            <div className="text-center py-12">
              <Server className="size-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No matching instances</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search or filter criteria
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project Name</TableHead>
                  <TableHead>GPU Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInstances.map((instance) => (
                  <TableRow key={instance.task_id}>
                    <TableCell className="font-medium">
                      {instance.project_name}
                    </TableCell>
                    <TableCell>{instance.gpu_type}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(instance.status) as any}>
                        {SSHInstanceStatusLabel[instance.status] || instance.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {instance.instance_ip || '-'}
                    </TableCell>
                    <TableCell>
                      {formatDate(instance.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        to={PATH_PAGE.dashboard.gpuInstance.byId(
                          workspaceId,
                          instance.task_id
                        ).overview}
                      >
                        <Button variant="ghost" size="sm">
                          View
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GPUInstanceList;
